// src/utils/points/groupReceipts.js
import { asInt, toDateMaybe } from "./format";

/**
 * Collapse the flat award_receipts stream into ledger items, grouping every
 * receipt that shares a capsule (meta.capsuleId) into a single line. The drop
 * reward and all per-recipient rewards for one capsule therefore render as one
 * combined "+N" row instead of scattering across many rows.
 *
 * Receipts without a capsuleId (e.g. insider_free_signup) pass through as their
 * own standalone items and render individually.
 *
 * The returned items are sorted by representative date descending, where a
 * capsule group's representative date is its MOST RECENT receipt — so a later
 * pickup bumps the capsule back to the top of the ledger (latest-activity sort).
 *
 * Input is assumed to already be sorted by createdAt desc (as usePointsReceipts
 * provides), but we sort defensively so ordering is correct regardless.
 */
export function groupReceiptsByCapsule(receipts) {
  const list = Array.isArray(receipts) ? receipts : [];

  const groupsByCapsule = new Map();
  const items = [];

  for (const r of list) {
    const capsuleId = r?.meta?.capsuleId;

    if (!capsuleId) {
      items.push({ ...r, sortMs: msOf(r?.createdAt) });
      continue;
    }

    let group = groupsByCapsule.get(capsuleId);
    if (!group) {
      group = {
        id: `capsule_${capsuleId}`,
        isGroup: true,
        capsuleId,
        receipts: [],
        totalPts: 0,
        reasons: new Set(),
        createdAt: r?.createdAt || null,
        sortMs: msOf(r?.createdAt),
      };
      groupsByCapsule.set(capsuleId, group);
      items.push(group);
    }

    group.receipts.push(r);
    group.totalPts += asInt(r?.deltaPoints, 0);
    if (r?.reason) group.reasons.add(r.reason);

    // Keep the most-recent receipt as the group's representative date.
    const ms = msOf(r?.createdAt);
    if (ms > group.sortMs) {
      group.sortMs = ms;
      group.createdAt = r?.createdAt || group.createdAt;
    }
  }

  // Finalize: expose reasons as an array for consumers.
  for (const g of groupsByCapsule.values()) {
    g.reasons = Array.from(g.reasons);
  }

  return items.sort((a, b) => b.sortMs - a.sortMs);
}

function msOf(createdAt) {
  const d = toDateMaybe(createdAt);
  return d ? d.getTime() : 0;
}
